import axios from "axios";
import jsonpAdapter from "axios-jsonp";
import cookies from "js-cookie";
import sharedConfig from "shared/config";
import { PersonaUserProfile } from "shared/helpers/users";
import { silentlyAuthenticate } from "@technologyfromsage/persona-padlock";
import { getPersonaConfig } from "../../AuthenticatedPage/config";

export type CurrentUserData = {
  guid: string;
  accessToken: string;
  scopes: string[];
  profile?: PersonaUserProfile;
  sessionId: string;
};

export interface LoginJsonUser {
  guid: string;
  oauth: {
    access_token: string;
    scope: string[];
    token_type: string;
  };
  profile?: PersonaUserProfile;
}

export const silentlyAuthenticateUserViaPersonaPadlock = async (
  authProvider: string,
): Promise<LoginJsonUser | undefined> => {
  try {
    const config = getPersonaConfig(authProvider);
    const loginResponse = await silentlyAuthenticate(config);

    return {
      guid: loginResponse.persona_guid,
      oauth: {
        access_token: loginResponse.access_token,
        scope: loginResponse.persona_scopes,
        token_type: "Bearer",
      },
      profile: loginResponse.persona_profile,
    };
  } catch (error) {
    return undefined;
  }
};

export const fetchAuthenticatedUser = async (): Promise<
  LoginJsonUser | undefined
> => {
  const doubleSubmit = cookies.get("doubleSubmit");
  const url = new URL("auth/login.json", sharedConfig.oauthOrigin);

  if (doubleSubmit) {
    url.searchParams.set("doubleSubmit", doubleSubmit);
  }

  return axios
    .get<LoginJsonUser | undefined>(url.toString(), {
      withCredentials: true,
      adapter: jsonpAdapter,
      callbackParamName: "cb", // this is the name of the variable in persona, changing it will break auth
    })
    .then((response): LoginJsonUser | undefined => response?.data);
};

export enum AuthScope {
  Read = "READ",
  Write = "WRITE",
  Support = "SUPPORT",
  Member = "MEMBER",
}

const generateScopeFromType = (scope: AuthScope, instCode: string): string => {
  switch (scope) {
    case AuthScope.Support:
      return "elevate:su";
    case AuthScope.Read:
      return `elevate:read@${instCode}`;
    case AuthScope.Write:
      return `elevate:write@${instCode}`;
    case AuthScope.Member:
      return `member@${instCode}`;
  }
};

export const userHasRequiredScopes = (
  requiredScopes: AuthScope[],
  currentUser: CurrentUserData,
  instCode: string,
): boolean =>
  requiredScopes
    .map((scope) => generateScopeFromType(scope, instCode))
    .every((scope) => currentUser.scopes.includes(scope));

export const userHasSufficientScopes = (
  requiredScopes: AuthScope[],
  currentUser: CurrentUserData,
  instCode: string,
): boolean =>
  isAdmin(currentUser) ||
  userHasRequiredScopes(requiredScopes, currentUser, instCode);

export const isAdmin = (currentUser: CurrentUserData): boolean =>
  currentUser.scopes.includes(
    generateScopeFromType(AuthScope.Support, sharedConfig.adminAuthProvider),
  );

export const isAcademic = (
  currentUser: CurrentUserData,
  instCode: string,
): boolean => userHasSufficientScopes([AuthScope.Write], currentUser, instCode);

export const isStudent = (
  currentUser: CurrentUserData,
  instCode: string,
): boolean => !isAcademic(currentUser, instCode) && !isAdmin(currentUser);

export const isSupportMode = (
  currentUser: CurrentUserData,
  instCode: string,
): boolean =>
  currentUser.scopes.includes(
    generateScopeFromType(AuthScope.Support, sharedConfig.adminAuthProvider),
  ) && !userHasRequiredScopes([AuthScope.Member], currentUser, instCode);

export const getInstCodeFromScopes = (scopes: Array<string>): string | null => {
  const filterOutDevolvedConstraints = (scope: string) => !scope.includes(":");

  const memberScope = scopes
    .filter((scope) => scope.includes("member@"))
    .find(filterOutDevolvedConstraints);

  if (memberScope) {
    return memberScope.split("@")[1];
  }

  return null;
};
